import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAudio } from '../context/AudioContext';
import { useTheme } from '../context/ThemeContext';
import { useSeasonalTheme } from '../hooks/useSeasonalTheme';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { TrackArtwork } from '../components/TrackArtwork';
import { LiveRadioPlayer } from '../components/LiveRadioPlayer';
import SongWarsChampionsSlideshow from '../components/SongWarsChampionsSlideshow';
import CreatorSpotlight from '../components/CreatorSpotlight';
import FeaturedPromotions from '../components/FeaturedPromotions';
import GoldFrameCard from '../components/GoldFrameCard';
import FeaturedBannerCarousel from '../components/FeaturedBannerCarousel';
import SEOHead from '../components/common/SEOHead';
import EngagementChecklist from '../components/engagement/EngagementChecklist';
import { LazyImage } from '../components/common/LazyImage';
import { ContentPlaceholder } from '../components/common/ContentPlaceholder';
import { getProfilePicUrl } from '../utils/profilePic';
import { PromoteButton } from '../components/PromoteModal';
import TrendingSection from '../components/home/TrendingSection';
import UpcomingReleases from '../components/UpcomingReleases';
import OnAirNowTicker from '../components/home/OnAirNowTicker';
import EarnWhileYouListenPromo from '../components/home/EarnWhileYouListenPromo';
import UpcomingSessionsStrip from '../components/home/UpcomingSessionsStrip';
import ListeningPartiesHomeStrip from '../components/home/ListeningPartiesHomeStrip';
import OneVoiceRadarStrip from '../components/home/OneVoiceRadarStrip';
import OneVoiceTodayStrip from '../components/home/OneVoiceTodayStrip';
import { UserAvatar } from '../components/ui/UserAvatar';
import { 
  Play, Pause, Music, Video, TrendingUp, Heart, Gift, Users, 
  Radio, Disc, Bot, Sparkles, Trophy, ArrowRight, Crown, Zap, Headphones, 
  ExternalLink, Upload, Plus, BarChart2, DollarSign, Eye, Calendar,
  Swords, MessageCircle, Award, Star
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Platform icons/colors for livestreams
const PLATFORM_CONFIG = {
  tiktok: { color: '#000000', bg: '#00f2ea' },
  youtube: { color: '#ffffff', bg: '#ff0000' },
  instagram: { color: '#ffffff', bg: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' },
  twitch: { color: '#ffffff', bg: '#9146ff' }
};

// Default home-page section order (admin can reorder via the home-page
// manager tab in the admin panel). Sections not present in
// `section_order` from the backend fall back to this list.
export const DEFAULT_SECTION_ORDER = [
  'featured_banner',
  'on_air_ticker',
  'earn_while_listen',
  'welcome_hero',
  'engagement_checklist',
  'upcoming_sessions',
  'listening_parties',
  'onevoice_today',
  'radar',
  'creator_stats',
  'radio_buttons',
  'active_livestreams',
  'song_wars_champions',
  'creator_spotlight',
  'featured_promotions',
  'hot_events',
  'upcoming_releases',
  'premium_content',
  'free_tracks',
  'free_videos',
  'popular_artists',
  'popular_producers',
  'ai_spotlight',
  'weekly_top_tracks',
];

// Human-readable labels shown in the admin layout panel.
export const SECTION_LABELS = {
  featured_banner: 'Featured Banner Carousel',
  on_air_ticker: 'On-Air Now Ticker',
  earn_while_listen: 'Earn While You Listen Promo',
  welcome_hero: 'Welcome Header',
  engagement_checklist: 'Engagement Checklist',
  upcoming_sessions: 'Upcoming Review Sessions',
  listening_parties: 'Listening Parties Strip',
  onevoice_today: 'OneVoice Today (Editorial)',
  radar: 'OneVoice Radar (Algorithmic)',
  creator_stats: 'Your Creator Stats',
  radio_buttons: 'Listen Now (Radio Stations)',
  active_livestreams: 'Active Livestreams',
  song_wars_champions: 'Song Wars Champions',
  creator_spotlight: 'Creator Spotlight',
  featured_promotions: 'Featured Promotions',
  hot_events: 'Hot Events',
  upcoming_releases: 'Upcoming Releases',
  premium_content: 'Premium Content',
  free_tracks: 'Free Tracks',
  free_videos: 'Free Videos',
  popular_artists: 'Popular Artists',
  popular_producers: 'Top Producers',
  ai_spotlight: 'Weekly AI Artist Spotlight',
  weekly_top_tracks: 'Weekly Top Tracks CTA',
};

export default function Home() {
  const [featured, setFeatured] = useState({ featured_tracks: [], featured_videos: [], free_tracks: [], free_videos: [] });
  const [popularArtists, setPopularArtists] = useState([]);
  const [popularProducers, setPopularProducers] = useState([]);
  const [aiContent, setAiContent] = useState({ tracks: [], artists: [], spotlight: null });
  const [activeLivestreams, setActiveLivestreams] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [hotEvents, setHotEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState({ section_visibility: {}, section_order: [] });
  const { user, token } = useAuth();
  const { playTrack, currentTrack, isPlaying, togglePlay } = useAudio();
  const { mode } = useTheme();
  const seasonal = useSeasonalTheme();
  const navigate = useNavigate();

  // Tour is now handled by OnboardingFlow in App.js

  useEffect(() => {
    if (user) {
      fetchData();
      fetchUserStats();
    }
    // iter_474 — Pause livestream polling while tab is hidden.
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchLivestreams();
    }, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchLivestreams(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [user]);

  // Fetch admin-configured layout (visibility + ordering). Failure is
  // silent: we fall back to defaults so the home page renders normally.
  // If this Home is loaded inside an admin "Preview as fan" iframe, the
  // parent will postMessage the unsaved layout; we honour that and skip
  // the network fetch.
  useEffect(() => {
    let alive = true;
    const inIframe = window.parent && window.parent !== window;
    // Listen for preview-mode messages from the admin parent window.
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== 'home-preview-layout') return;
      setLayout({
        section_visibility: data.section_visibility || {},
        section_order: data.section_order || [],
        _preview: true,
      });
    };
    window.addEventListener('message', onMessage);
    // While inside the preview iframe, forward Escape to the parent
    // so it can close the modal even when the iframe has focus.
    const onKey = (e) => {
      if (e.key === 'Escape' && inIframe) {
        try {
          window.parent.postMessage({ type: 'home-preview-close' }, window.location.origin);
        } catch (_e) { /* parent gone */ }
      }
    };
    window.addEventListener('keydown', onKey);
    // Signal to the parent that we are ready to receive a layout.
    try {
      if (inIframe) {
        window.parent.postMessage({ type: 'home-preview-ready' }, window.location.origin);
      }
    } catch (_e) { /* cross-origin parent — safe to ignore */ }

    // Fall back to remote fetch (only used when NOT in preview mode).
    axios.get(`${API}/home-page/layout`)
      .then(res => { if (alive && res.data) setLayout(prev => prev._preview ? prev : {
        section_visibility: res.data.section_visibility || {},
        section_order: res.data.section_order || [],
      }); })
      .catch(() => {});
    return () => {
      alive = false;
      window.removeEventListener('message', onMessage);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const fetchUserStats = async () => {
    if (!token) return;
    try {
      // Fetch user's content stats
      const [tracksRes, videosRes, earningsRes] = await Promise.all([
        axios.get(`${API}/tracks/user/${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/videos/user/${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/earnings/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      const tracks = tracksRes.data || [];
      const videos = videosRes.data || [];
      
      setUserStats({
        trackCount: tracks.length,
        videoCount: videos.length,
        totalPlays: tracks.reduce((sum, t) => sum + (t.play_count || 0), 0),
        totalViews: videos.reduce((sum, v) => sum + (v.view_count || 0), 0),
        totalLikes: tracks.reduce((sum, t) => sum + (t.like_count || 0), 0) + 
                    videos.reduce((sum, v) => sum + (v.like_count || 0), 0),
        earnings: earningsRes.data?.total || 0,
        pendingPayout: earningsRes.data?.pending || 0
      });
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchLivestreams = async () => {
    try {
      const response = await axios.get(`${API}/livestream/active`);
      setActiveLivestreams(response.data.livestreams || []);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchData = async () => {
    try {
      const [featuredRes, artistsRes, producersRes, aiArtistsRes, livestreamsRes, hotEventsRes, aiSpotlightRes] = await Promise.all([
        axios.get(`${API}/browse/featured`),
        axios.get(`${API}/discover/popular-artists`).catch(() => ({ data: [] })),
        axios.get(`${API}/discover/popular-producers`).catch(() => ({ data: [] })),
        axios.get(`${API}/leaderboard/ai-artists?limit=6&period=week`).catch(() => ({ data: { leaderboard: [] } })),
        axios.get(`${API}/livestream/active`).catch(() => ({ data: { livestreams: [] } })),
        axios.get(`${API}/events/hot`).catch(() => ({ data: [] })),
        axios.get(`${API}/weekly-spotlight/ai-artist`).catch(() => ({ data: { spotlight: null } }))
      ]);
      setFeatured(featuredRes.data);
      setPopularArtists(artistsRes.data);
      setPopularProducers(producersRes.data);
      setActiveLivestreams(livestreamsRes.data.livestreams || []);
      setHotEvents(Array.isArray(hotEventsRes.data) ? hotEventsRes.data : []);
      
      const aiArtists = aiArtistsRes.data.leaderboard || [];
      const weeklySpotlight = aiSpotlightRes.data?.spotlight || aiArtists[0] || null;
      setAiContent({
        tracks: [],
        artists: aiArtists.slice(0, 6),
        spotlight: weeklySpotlight
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayTrack = (track, playlist) => {
    if (currentTrack?.id === track.id) {
      togglePlay();
    } else {
      playTrack(track, playlist);
    }
  };

  const isCreator = user?.is_artist || user?.is_producer || user?.is_ai_artist;

  // Build a section → JSX map. We declare every section as a function
  // so React only evaluates the JSX we actually render.
  const sections = {
    featured_banner: () => <FeaturedBannerCarousel />,
    on_air_ticker: () => <OnAirNowTicker />,
    earn_while_listen: () => <EarnWhileYouListenPromo />,
    welcome_hero: () => (
      <section
        className="relative px-6 md:px-8 py-10 md:py-14 border-b border-[#1a1a1a] overflow-hidden"
        data-testid="home-hero-section"
      >
        {/* Theme-based hero background */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700"
          style={{
            backgroundImage: mode === 'light'
              ? 'url(/clouds-bg.jpg)'
              : 'url(/moon-bg.jpg)',
            opacity: 1
          }}
        />
        <div className={`absolute inset-0 ${
          mode === 'light'
            ? 'bg-gradient-to-b from-white/60 via-white/40 to-white/80'
            : 'bg-gradient-to-b from-black/50 via-black/30 to-black/70'
        }`} />
        {seasonal.isHoliday && (
          <div className={`absolute inset-0 bg-gradient-to-br ${seasonal.heroGradient} pointer-events-none`} />
        )}
        <div className="max-w-[1600px] mx-auto relative z-10">
          {seasonal.isHoliday && (
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-4 border font-mono text-[10px] tracking-widest uppercase"
              style={{
                borderColor: `${seasonal.accent}40`,
                color: mode === 'light' ? (seasonal.lightAccent?.color || seasonal.accent) : seasonal.accent,
                background: mode === 'light' ? (seasonal.lightAccent?.bg || `${seasonal.accent}10`) : `${seasonal.accent}15`
              }}
              data-testid="seasonal-banner"
            >
              {seasonal.label}
            </div>
          )}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className={`font-mono text-sm tracking-widest uppercase mb-1 ${
                mode === 'light' ? '!text-[#0077b6]' : 'text-[#00CCFF]'
              }`} style={seasonal.isHoliday ? { color: mode === 'light' ? (seasonal.lightAccent?.color || seasonal.accent) : seasonal.accent } : {}}>
                // WELCOME BACK
              </p>
              <h1
                className={`glow-h1 text-3xl md:text-4xl font-['Chakra_Petch'] font-bold uppercase ${mode === 'light' ? '!text-[#0f172a]' : 'text-white'}`}
                style={{ '--glow-h1-color': mode === 'light' ? '#0077b6' : '#00CCFF' }}
                data-testid="home-welcome-title"
              >
                {user?.username}
              </h1>
              <p className={`font-mono text-sm mt-2 ${
                mode === 'light' ? '!text-[#475569]' : 'text-[#c0c0c0]'
              }`}>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {isCreator && (
                <Button
                  onClick={() => navigate('/upload')}
                  className="bg-[#00CCFF] text-black font-['Chakra_Petch'] uppercase hover:shadow-[0_0_15px_rgba(0,204,255,0.4)] transition-all"
                  data-testid="hero-upload-btn"
                >
                  <Upload size={16} className="mr-2" /> Upload
                </Button>
              )}
              <Button
                onClick={() => navigate('/music-review')}
                className="bg-[#FF69B4] text-black font-['Chakra_Petch'] uppercase hover:shadow-[0_0_15px_rgba(255,105,180,0.4)] transition-all"
                data-testid="hero-song-wars-btn"
              >
                <Swords size={16} className="mr-2" /> Song Wars
              </Button>
              <Button
                onClick={() => navigate('/browse')}
                variant="outline"
                className={`font-['Chakra_Petch'] uppercase ${
                  mode === 'light'
                    ? 'border-[#94a3b8] !text-[#0f172a] hover:border-[#0077b6]'
                    : 'border-[#333] text-white hover:border-white'
                }`}
                data-testid="hero-browse-btn"
              >
                <Music size={16} className="mr-2" /> Browse
              </Button>
            </div>
          </div>
        </div>
      </section>
    ),
    engagement_checklist: () => token ? (
      <section className="px-6 md:px-8 py-6">
        <div className="max-w-[1600px] mx-auto">
          <EngagementChecklist />
        </div>
      </section>
    ) : null,
    upcoming_sessions: () => <UpcomingSessionsStrip />,
    listening_parties: () => <ListeningPartiesHomeStrip />,
    onevoice_today: () => (
      <section className="px-6 md:px-8 py-6">
        <div className="max-w-[1600px] mx-auto">
          <OneVoiceTodayStrip />
        </div>
      </section>
    ),
    radar: () => (
      <section className="border-b border-[#1a1a1a] bg-[#050505]" data-testid="home-radar-section">
        <OneVoiceRadarStrip />
      </section>
    ),
    creator_stats: () => (isCreator && userStats) ? (
      <section className="px-6 md:px-8 py-8 bg-black border-b border-[#1a1a1a]">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase mb-6 flex items-center gap-2">
            <span className="section-title-accent section-title-cyan"><BarChart2 size={20} /> Your Stats</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="p-4 bg-[#111] border border-[#1a1a1a] hover:border-[#00CCFF] transition-colors">
              <Music size={20} className="text-[#00CCFF] mb-2" />
              <p className="text-2xl font-['Chakra_Petch'] text-white font-bold">{userStats.trackCount}</p>
              <p className="text-[#888] font-mono text-xs uppercase">Tracks</p>
            </div>
            <div className="p-4 bg-[#111] border border-[#1a1a1a] hover:border-[#00CCFF] transition-colors">
              <Video size={20} className="text-[#00CCFF] mb-2" />
              <p className="text-2xl font-['Chakra_Petch'] text-white font-bold">{userStats.videoCount}</p>
              <p className="text-[#888] font-mono text-xs uppercase">Videos</p>
            </div>
            <div className="p-4 bg-[#111] border border-[#1a1a1a] hover:border-[#00ff88] transition-colors">
              <Play size={20} className="text-[#00ff88] mb-2" />
              <p className="text-2xl font-['Chakra_Petch'] text-white font-bold">{userStats.totalPlays.toLocaleString()}</p>
              <p className="text-[#888] font-mono text-xs uppercase">Plays</p>
            </div>
            <div className="p-4 bg-[#111] border border-[#1a1a1a] hover:border-[#00ff88] transition-colors">
              <Eye size={20} className="text-[#00ff88] mb-2" />
              <p className="text-2xl font-['Chakra_Petch'] text-white font-bold">{userStats.totalViews.toLocaleString()}</p>
              <p className="text-[#888] font-mono text-xs uppercase">Views</p>
            </div>
            <div className="p-4 bg-[#111] border border-[#1a1a1a] hover:border-[#ff69b4] transition-colors">
              <Heart size={20} className="text-[#ff69b4] mb-2" />
              <p className="text-2xl font-['Chakra_Petch'] text-white font-bold">{userStats.totalLikes.toLocaleString()}</p>
              <p className="text-[#888] font-mono text-xs uppercase">Likes</p>
            </div>
            <div className="p-4 bg-[#111] border border-[#FFDA33]/30 hover:border-[#FFDA33] transition-colors">
              <DollarSign size={20} className="text-[#FFDA33] mb-2" />
              <p className="text-2xl font-['Chakra_Petch'] text-[#FFDA33] font-bold">${userStats.earnings.toFixed(2)}</p>
              <p className="text-[#888] font-mono text-xs uppercase">Earnings</p>
            </div>
          </div>
        </div>
      </section>
    ) : null,
    radio_buttons: () => renderRadioButtons(),
    active_livestreams: () => activeLivestreams.length > 0 ? renderActiveLivestreams() : null,
    song_wars_champions: () => (
      <section className="bg-black border-b border-[#1a1a1a]">
        <SongWarsChampionsSlideshow />
      </section>
    ),
    creator_spotlight: () => (
      <section className="border-b border-[#1a1a1a] bg-[#050505]">
        <CreatorSpotlight />
      </section>
    ),
    featured_promotions: () => <FeaturedPromotions />,
    hot_events: () => hotEvents.length > 0 ? renderHotEvents() : null,
    upcoming_releases: () => <UpcomingReleases />,
    premium_content: () => (featured.featured_tracks?.length > 0 || featured.featured_videos?.length > 0) ? renderPremiumContent() : null,
    free_tracks: () => featured.free_tracks?.length > 0 ? renderFreeTracks() : null,
    free_videos: () => featured.free_videos?.length > 0 ? renderFreeVideos() : null,
    popular_artists: () => popularArtists.length > 0 ? renderPopularArtists() : null,
    popular_producers: () => popularProducers.length > 0 ? renderPopularProducers() : null,
    ai_spotlight: () => aiContent.spotlight ? renderAiSpotlight() : null,
    weekly_top_tracks: () => renderWeeklyTopTracks(),
  };

  // Compose the effective render order: any keys configured by admin
  // first (in their chosen order), then any remaining defaults that
  // weren't explicitly placed. This makes the layout forward-compatible
  // when we add new sections.
  const adminOrder = (layout.section_order || []).filter(k => DEFAULT_SECTION_ORDER.includes(k));
  const remaining = DEFAULT_SECTION_ORDER.filter(k => !adminOrder.includes(k));
  const effectiveOrder = [...adminOrder, ...remaining];
  // Visibility: default ON; admin can hide by setting `false`.
  const isVisible = (key) => layout.section_visibility?.[key] !== false;

  // Heavy section renderers extracted for readability — these are
  // re-entered every render but only when their parent is in the
  // visible order. React reconciles them efficiently.
  function renderRadioButtons() {
    return (
      <section className="preserve-dark px-6 md:px-8 py-10 border-b border-[#1a1a1a] relative overflow-hidden bg-[#050505]" data-testid="listen-now-section">
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-70">
          <div className="absolute -top-24 left-1/4 w-[520px] h-[520px] rounded-full bg-[#00CCFF]/10 blur-3xl" />
          <div className="absolute -bottom-32 right-1/4 w-[520px] h-[520px] rounded-full bg-[#FFDA33]/10 blur-3xl" />
        </div>
        <div className="max-w-[1600px] mx-auto relative">
          <div className="mb-8 text-center md:text-left" data-testid="listen-now-headline">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-3 border border-[#FFDA33]/30 bg-[#FFDA33]/5">
              <Radio size={12} className="text-[#FFDA33] animate-pulse" />
              <span className="text-[#FFDA33] font-mono text-[10px] uppercase tracking-widest font-bold">Listen Now</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-['Chakra_Petch'] text-white uppercase">
              <span className="section-title-accent section-title-cyan">Radio Stations</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Button
              onClick={() => navigate('/radio/all')}
              className="h-16 md:h-20 bg-gradient-to-br from-[#00CCFF] to-[#0099CC] text-black font-['Chakra_Petch'] uppercase tracking-wider hover:shadow-[0_0_24px_rgba(0,204,255,0.5)] transition-all"
              data-testid="listen-all-genre-btn"
            >
              <Radio size={18} className="mr-2" /> All Genre
            </Button>
            <Button
              onClick={() => navigate('/radio/music')}
              className="h-16 md:h-20 bg-gradient-to-br from-[#FFDA33] to-[#ff9900] text-black font-['Chakra_Petch'] uppercase tracking-wider hover:shadow-[0_0_24px_rgba(255,218,51,0.5)] transition-all"
              data-testid="listen-music-btn"
            >
              <Radio size={18} className="mr-2" /> Music Radio
            </Button>
            <Button
              onClick={() => navigate('/radio/beats')}
              className="h-16 md:h-20 bg-gradient-to-br from-[#ff6b6b] to-[#cc0000] text-white font-['Chakra_Petch'] uppercase tracking-wider hover:shadow-[0_0_24px_rgba(255,107,107,0.5)] transition-all"
              data-testid="listen-beats-btn"
            >
              <Headphones size={18} className="mr-2" /> Beats Radio
            </Button>
            <Button
              onClick={() => navigate('/radio/ai')}
              className="h-16 md:h-20 bg-gradient-to-br from-[#9b59b6] to-[#6a0dad] text-white font-['Chakra_Petch'] uppercase tracking-wider hover:shadow-[0_0_24px_rgba(155,89,182,0.5)] transition-all"
              data-testid="listen-ai-btn"
            >
              <Bot size={18} className="mr-2" /> AI Radio
            </Button>
          </div>
          <div className="mt-6">
            <LiveRadioPlayer />
          </div>
        </div>
      </section>
    );
  }

  function renderActiveLivestreams() {
    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]" data-testid="active-livestreams">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="relative">
              <Radio size={20} className="text-[#ff0000]" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#ff0000] rounded-full animate-pulse" />
            </div>
            <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
              <span className="section-title-accent section-title-red"><Radio size={20} /> Live Now</span>
            </h2>
            <span className="text-[#ff0000] font-mono text-sm">{activeLivestreams.length} streaming</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {activeLivestreams.map((stream) => {
              const platformConfig = PLATFORM_CONFIG[stream.platform] || { bg: '#333', color: '#fff' };
              return (
                <a
                  key={stream.id}
                  href={stream.stream_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-[#ff0000]/30 bg-black hover:border-[#ff0000] transition-all overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#ff0000]">
                          {stream.profile_picture ? (
                            <img
                              src={getProfilePicUrl(stream.profile_picture, stream.user_id || stream.id)}
                              alt={stream.username}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#ff0000] to-[#cc0000] flex items-center justify-center">
                              <span className="text-white font-bold">
                                {stream.username?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="absolute -bottom-1 -right-1 px-1 py-0.5 bg-[#ff0000] text-white text-[8px] font-bold uppercase">
                          LIVE
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-['Chakra_Petch'] font-semibold truncate text-sm">
                          {stream.username}
                        </p>
                        <p className="text-[#888] font-mono text-xs truncate">
                          {stream.stream_title || `Streaming on ${stream.platform}`}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    className="px-4 py-2 flex items-center justify-between"
                    style={{ background: platformConfig.bg }}
                  >
                    <span
                      className="font-mono text-xs uppercase font-bold"
                      style={{ color: platformConfig.color }}
                    >
                      {stream.platform}
                    </span>
                    <ExternalLink size={12} style={{ color: platformConfig.color }} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  function renderHotEvents() {
    // Format date/time helpers with graceful TBA fallback.
    const isTBA = (v) => !v || (typeof v === 'string' && v.trim().toUpperCase() === 'TBA');
    const fmtDate = (d) => {
      if (isTBA(d)) return 'TBA';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return 'TBA';
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    const fmtTime = (t) => (isTBA(t) ? 'TBA' : t);

    // Resolve the flyer, preferring the `images[0]` gallery entry so admin
    // uploads flow through here. Relative `/api/...` URLs get the backend
    // host prefix so they render everywhere.
    const resolveImg = (ev) => {
      const raw = (Array.isArray(ev.images) && ev.images[0]) || ev.image_url || ev.cover_image;
      if (!raw) return null;
      if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
      return `${BACKEND_URL}${raw}`;
    };

    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]" data-testid="hot-events-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <TrendingUp size={20} className="text-[#ff6b6b]" />
              <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase tracking-wider">
                <span className="section-title-accent section-title-red"><TrendingUp size={20} /> Hot Events</span>
              </h2>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/events')}
              className="text-[#888] hover:text-white font-mono text-xs uppercase"
            >
              View All <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {hotEvents.map((event, idx) => {
              const img = resolveImg(event);
              return (
                <div
                  key={event.id}
                  onClick={() => navigate(`/events/${event.id}`)}
                  className="border border-[#1a1a1a] bg-[#0a0a0a] hover:border-[#ff6b6b]/40 transition-all cursor-pointer group relative overflow-hidden flex flex-col"
                  data-testid={`hot-event-${idx}`}
                >
                  {/* Flyer with smart-focus. The CSS `attention()` value is
                      not yet supported by browsers; the `center 30%`
                      fallback frames the top-third of the poster which is
                      where the title/subject usually sits.  A blurred
                      backdrop fills the letterbox for off-ratio flyers. */}
                  <div className="relative w-full aspect-[16/9] bg-black overflow-hidden">
                    {img ? (
                      <>
                        <div
                          className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-40"
                          style={{ backgroundImage: `url(${img})` }}
                          aria-hidden="true"
                        />
                        <img
                          src={img}
                          alt={event.title || event.name || 'Event flyer'}
                          className="hot-event-flyer w-full h-full object-contain relative z-[1] transition-transform group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#333]">
                        <Calendar size={40} />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 z-[2] w-8 h-8 flex items-center justify-center border border-[#ff6b6b]/60 bg-black/60 backdrop-blur-sm text-[#ff6b6b] font-mono text-sm font-bold rounded">
                      #{idx + 1}
                    </div>
                  </div>

                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-[#ff6b6b] font-mono text-xs uppercase tracking-widest mb-1 flex items-center gap-1">
                      <Calendar size={12} /> {fmtDate(event.event_date)}
                      {(event.event_time || isTBA(event.event_time)) && (
                        <span className="text-[#888] normal-case">· {fmtTime(event.event_time)}</span>
                      )}
                    </p>
                    <h3 className="text-white font-['Chakra_Petch'] text-base uppercase pr-2 line-clamp-2 group-hover:text-[#ff6b6b] transition-colors">
                      {event.title || event.name}
                    </h3>
                    {event.description && (
                      <p className="text-[#00CCFF] font-mono text-xs mt-1 line-clamp-2">
                        {event.description}
                      </p>
                    )}
                    <p className="text-[#666] font-mono text-xs mt-2 truncate">
                      📍 {event.venue_name || 'TBA'}
                      {event.venue_address && event.venue_address !== event.venue_name && (
                        <span className="text-[#555]"> · {event.venue_address}</span>
                      )}
                    </p>

                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#111]">
                      <span className="text-[#ff6b6b] font-mono text-xs flex items-center gap-1">
                        <Users size={12} /> {event.tickets_sold || 0} sold
                      </span>
                      <span className="text-[#888] font-mono text-xs">
                        ${event.ticket_price?.toFixed(2) || '0.00'}
                      </span>
                      <span className="text-[#555] font-mono text-xs ml-auto truncate">
                        by {event.off_platform_organizer?.name || event.organizer_username}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  function renderPremiumContent() {
    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]" data-testid="premium-content-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Crown size={20} className="text-[#FFDA33]" />
              <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
                <span className="section-title-accent section-title-gold"><Crown size={20} /> Premium Content</span>
              </h2>
            </div>
            <Button
              onClick={() => navigate('/browse?filter=paid')}
              variant="ghost"
              className="text-[#FFDA33] hover:text-white font-mono uppercase text-sm"
            >
              Browse All <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {featured.featured_tracks.slice(0, 8).map((track) => (
              <GoldFrameCard
                key={track.id}
                type="track"
                item={track}
                onClick={() => navigate(`/track/${track.slug || track.id}`)}
              />
            ))}
            {featured.featured_videos?.slice(0, 4).map((video) => (
              <GoldFrameCard
                key={video.id}
                type="video"
                item={video}
                onClick={() => navigate(`/videos/${video.id}`)}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  function renderFreeTracks() {
    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Gift size={20} className="text-[#00ff88]" />
              <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
                <span className="section-title-accent section-title-green"><Gift size={20} /> Free Tracks</span>
              </h2>
            </div>
            <Button
              onClick={() => navigate('/free-player')}
              variant="ghost"
              className="text-[#00ff88] hover:text-white font-mono uppercase text-sm"
            >
              Free Radio <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
            {featured.free_tracks.slice(0, 6).map((track) => (
              <GoldFrameCard
                key={track.id}
                type="track"
                item={{...track, is_free: true}}
                onClick={() => navigate(`/track/${track.slug || track.id}`)}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  function renderFreeVideos() {
    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]" data-testid="free-videos-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Video size={20} className="text-[#00CCFF]" />
              <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
                <span className="section-title-accent section-title-cyan"><Video size={20} /> Free Videos</span>
              </h2>
            </div>
            <Button
              onClick={() => navigate('/free-videos')}
              variant="ghost"
              className="text-[#00CCFF] hover:text-white font-mono uppercase text-sm"
            >
              Watch All <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {featured.free_videos.slice(0, 5).map((video) => (
              <GoldFrameCard
                key={video.id}
                type="video"
                item={{...video, is_free: true}}
                onClick={() => navigate(`/videos/${video.id}`)}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  function renderPopularArtists() {
    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <TrendingUp size={20} className="text-[#00CCFF]" />
              <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
                <span className="section-title-accent section-title-cyan"><TrendingUp size={20} /> Popular Artists</span>
              </h2>
            </div>
            <Button
              onClick={() => navigate('/leaderboard')}
              variant="ghost"
              className="text-[#00CCFF] hover:text-white font-mono uppercase text-sm"
            >
              View All <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
            {popularArtists.slice(0, 6).map((artist) => (
              <GoldFrameCard
                key={artist.id}
                type="profile"
                item={artist}
                onClick={() => navigate(`/profile/${artist.id}`)}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  function renderPopularProducers() {
    return (
      <section className="px-6 md:px-8 py-8 border-b border-[#1a1a1a]">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Headphones size={20} className="text-[#ff6b6b]" />
              <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
                <span className="section-title-accent section-title-red"><Headphones size={20} /> Top Producers</span>
              </h2>
            </div>
            <Button
              onClick={() => navigate('/browse?tab=beats')}
              variant="ghost"
              className="text-[#ff6b6b] hover:text-white font-mono uppercase text-sm"
            >
              Browse Beats <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
            {popularProducers.slice(0, 6).map((producer) => (
              <GoldFrameCard
                key={producer.id}
                type="profile"
                item={producer}
                onClick={() => navigate(`/profile/${producer.id}`)}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  function renderAiSpotlight() {
    return (
      <section className="px-6 md:px-8 py-10 dark-surface" data-testid="weekly-ai-spotlight">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-lg font-['Chakra_Petch'] text-white uppercase">
              <span className="section-title-accent section-title-gold"><Crown size={20} /> Weekly AI Artist Spotlight</span>
            </h2>
          </div>
          <div
            className="ai-spotlight-aura relative overflow-hidden cursor-pointer group bg-[#0c0c10]"
            onClick={() => navigate(`/profile/${aiContent.spotlight.id}`)}
            data-testid="ai-spotlight-card"
          >
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#FFDA33]/[0.04] via-transparent to-[#9b59b6]/[0.04]" />
            <span aria-hidden className="ai-spotlight-particle hidden md:block" style={{ top: '18%', left: '8%', animationDelay: '0s' }} />
            <span aria-hidden className="ai-spotlight-particle hidden md:block" style={{ top: '32%', left: '14%', animationDelay: '1.2s' }} />
            <span aria-hidden className="ai-spotlight-particle hidden md:block" style={{ top: '72%', left: '10%', animationDelay: '2.4s', background: '#9b59b6', boxShadow: '0 0 10px #9b59b6, 0 0 18px rgba(155,89,182,0.6)' }} />
            <span aria-hidden className="ai-spotlight-particle hidden md:block" style={{ top: '22%', right: '12%', animationDelay: '0.6s' }} />
            <span aria-hidden className="ai-spotlight-particle hidden md:block" style={{ top: '64%', right: '8%', animationDelay: '1.8s', background: '#00CCFF', boxShadow: '0 0 10px #00CCFF, 0 0 18px rgba(0,204,255,0.6)' }} />
            <div className="relative p-6 md:p-8 lg:p-10 z-10">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                <div className="relative flex-shrink-0">
                  <div aria-hidden className="absolute -inset-3 rounded-full bg-[#FFDA33]/20 blur-xl animate-pulse" />
                  <div className="relative w-28 h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 border-[3px] border-[#FFDA33] overflow-hidden shadow-[0_0_32px_rgba(255,218,51,0.45),0_0_64px_rgba(255,218,51,0.18)] rounded-full bg-[#0a0a0a]">
                    <UserAvatar
                      username={aiContent.spotlight.username}
                      profilePicture={aiContent.spotlight.profile_picture}
                      size={144}
                      rounded=""
                      className="!w-full !h-full"
                      testId="ai-spotlight-avatar"
                    />
                  </div>
                  <div className="absolute -top-3 -right-3 bg-gradient-to-br from-[#FFDA33] to-[#ff9900] p-2 rounded-sm shadow-[0_0_18px_rgba(255,218,51,0.75)] rotate-[8deg] group-hover:rotate-[-4deg] transition-transform duration-500">
                    <Crown size={18} className="text-black" />
                  </div>
                  <div className="absolute -bottom-2 -left-2 px-2.5 py-1 bg-[#9b59b6] text-white text-[10px] font-mono uppercase font-bold tracking-widest shadow-[0_0_12px_rgba(155,89,182,0.7)]">
                    AI
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left min-w-0">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FFDA33]/20 border border-[#FFDA33]/60 mb-3 shadow-[0_0_12px_rgba(255,218,51,0.35)]">
                    <Trophy size={12} className="text-[#FFDA33]" />
                    <span className="text-[#FFDA33] text-[10px] font-mono uppercase tracking-[0.3em] font-bold">#1 AI Artist This Week</span>
                  </div>
                  <h3 className="text-white font-['Chakra_Petch'] uppercase text-2xl md:text-3xl lg:text-4xl font-black flex items-center justify-center md:justify-start gap-2 group-hover:text-[#FFDA33] transition-colors tracking-tight truncate">
                    <span className="truncate">{aiContent.spotlight.username}</span>
                    <Bot size={22} className="text-[#9b59b6] flex-shrink-0 drop-shadow-[0_0_8px_rgba(155,89,182,0.6)]" />
                  </h3>
                  <div className="flex items-center justify-center md:justify-start gap-5 mt-4 text-[#bbb] font-mono text-xs">
                    <span className="flex items-center gap-1.5"><Music size={13} className="text-[#9b59b6]" /> <span className="text-white font-bold">{aiContent.spotlight.track_count || 0}</span> tracks</span>
                    <span className="w-px h-4 bg-[#333]" />
                    <span className="flex items-center gap-1.5"><Play size={13} className="text-[#00CCFF]" /> <span className="text-white font-bold">{aiContent.spotlight.total_streams || 0}</span> streams</span>
                  </div>
                </div>
                <div className="text-center px-6 py-4 border-2 border-[#FFDA33] bg-gradient-to-br from-[#FFDA33]/15 to-[#ff9900]/10 flex-shrink-0 shadow-[0_0_24px_rgba(255,218,51,0.35),inset_0_0_16px_rgba(255,218,51,0.12)]">
                  <p className="text-[#FFDA33] font-['Chakra_Petch'] text-4xl md:text-5xl font-black drop-shadow-[0_0_12px_rgba(255,218,51,0.7)] leading-none">
                    {aiContent.spotlight.points}
                  </p>
                  <p className="text-[#FFDA33]/80 font-mono text-[10px] uppercase tracking-[0.4em] mt-2 font-bold">Points</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  function renderWeeklyTopTracks() {
    return (
      <section className="px-4 md:px-8 py-8 dark-surface">
        <div className="max-w-[1600px] mx-auto">
          <div
            onClick={() => navigate('/top-tracks')}
            className="p-6 bg-gradient-to-r from-[#ff4444]/10 via-[#0a0a0a] to-[#FFDA33]/10 border border-[#1a1a1a] hover:border-[#ff4444]/40 cursor-pointer transition-all group"
            data-testid="weekly-top-tracks-cta"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#ff4444] to-[#FFDA33] flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={24} className="text-black" />
                </div>
                <div>
                  <h3 className="text-white font-['Chakra_Petch'] uppercase text-base sm:text-lg">Weekly Top Tracks</h3>
                  <p className="text-[#888] font-mono text-xs">The hottest tracks this week — updated every Monday</p>
                </div>
              </div>
              <ArrowRight size={20} className="text-[#888] group-hover:text-[#ff4444] transition-colors" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <Layout>
      <SEOHead title="Home" description="Your music dashboard. Browse trending tracks, discover new artists, and manage your music." />
      <div className="preserve-dark min-h-screen bg-[#0a0a0a]" data-testid="home-dashboard">
        {layout._preview && (
          <div
            className="px-4 py-2 bg-[#FFDA33] text-black flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest font-bold"
            data-testid="home-preview-banner"
          >
            <span>👁 Preview Mode — Showing unsaved layout</span>
          </div>
        )}
        {effectiveOrder.map(key => isVisible(key) ? (
          <Fragment key={key}>{sections[key] ? sections[key]() : null}</Fragment>
        ) : null)}
      </div>
    </Layout>
  );
}
